/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import { Button, Modal } from '@scuf/common';
import React, { useState, useContext } from 'react';
import CustomStepper from '../shared/CustomStepper/CustomStepper';
import './UtilityServiceCommon.scss';
import ChooseServiceTab from './ChooseServiceTab/ChooseServiceTab';
import descriptors from '../../locales/en-US/descriptors';
import ConnectTab from './ConnectTab/ConnectTab';
import useConnectorForPost from '../shared/useConnectorAndDynamicProps/useConnectorForPost';
import {
  IAllConnectDetails,
  ICommonServiceProps,
  IUtilityProviders,
  UtilityTypeNumber
} from './IUtilityService';
import ProgressTab from './ProgressTab/ProgressTab';
import UtilityAccountAction from '../../context/UtilityAccountContext/UtilityAccountActions';
import { useUtilityAccountContext } from '../../context/UtilityAccountContext/UtilityAccountContext';
import { useTranslation } from '@scuf/localization';
import { isObjectEmpty, validateConnecttabInput } from '../shared/Utils/Utils';
import { MAGIC_VALUE } from '../shared/Utils/Constants';
import { ApiContext } from '../../context/ApiContext';
import { IAny } from '../DashboardKPIWidgets/IEnergyManagementProps';
import { getEnrollNotifyInfo } from '../shared/Utils/UtilitySettings/UtilitySettingsUtils';
const indexes = {
  SearchTab: 0,
  ConnectTab: 1,
  ProgressTab: 2
};

const getUtilityCount = (
  utilityType: UtilityTypeNumber,
  UtilityArray: Array<string>
) => {
  return utilityType
    ? utilityType[UtilityArray[MAGIC_VALUE.THREE] as keyof typeof utilityType]
    : 0;
};

function UtilityServiceModal(props: ICommonServiceProps) {
  const d = descriptors.energyManagement;
  const [t] = useTranslation();
  const { bearerToken } = props;
  const [state, dispatch] = useUtilityAccountContext();
  const { showModal } = state;
  let { requestContext, utilityType } = props;
  const closeModal = () => {
    setActiveIndex(indexes.SearchTab);
    setAllConnectDetails(initDetails);
    SetSelectedUtilityProvider({} as IUtilityProviders);
    setSubmitted(false);
    dispatch(UtilityAccountAction.hideUtilityModal());
  };

  const emptyAccountDetails = {
    accountNumber: '',
    utility: '',
    accountNumberError: '',
    utilityError: ''
  };
  const initDetails = {
    accountDetails: [emptyAccountDetails],
    username: '',
    usernameError: '',
    password: '',
    passwordError: ''
  };
  const [activeIndex, setActiveIndex] = useState(indexes.SearchTab);
  const [allConnectDetails, setAllConnectDetails] = useState<
    IAllConnectDetails
  >(initDetails);
  const [SelectedUtilityProvider, SetSelectedUtilityProvider] = useState<
    IUtilityProviders
  >({} as IUtilityProviders);
  const [isSubmitted, setSubmitted] = useState(false);
  const [isDisabled, setDisabled] = useState(false);
  const chooseServiceProps = {
    bearerToken: bearerToken,
    requestContext: requestContext,
    SetSelectedUtilityProvider: SetSelectedUtilityProvider,
    SelectedUtilityProvider: SelectedUtilityProvider
  };
  const ProgressTabProps = {
    closeModal: closeModal
  };
  const connectorConfig = {
    name: 'enroll-utility-accounts'
  };
  const dataConnector = useConnectorForPost({
    connectorConfig,
    requestContext,
    bearerToken
  });
  const validateMandatoryFields = () => {
    const { isValid, tempValidation } = validateConnecttabInput(
      allConnectDetails
    );
    setAllConnectDetails({ ...tempValidation });
    return isValid;
  };

  const changeUtility = (UtilityTypes: string) => {
    const UtilityArray = UtilityTypes.split('"');
    // const count = utilityType
    //   ? utilityType[UtilityArray[MAGIC_VALUE.THREE] as keyof typeof utilityType]
    //   : 0;
    const count = getUtilityCount(utilityType, UtilityArray);
    const accountLimit = parseInt(
      window?.ENV?.EMAAS_ACCOUNT_LIMIT,
      MAGIC_VALUE.TEN
    );
    if (count && count > accountLimit) {
      setDisabled(true);
    } else {
      setDisabled(false);
    }
  };
  const disableButton = (val: boolean) => setDisabled(val);

  const connectProps = {
    allConnectDetails: allConnectDetails,
    setAllConnectDetails: setAllConnectDetails,
    isSubmitted: isSubmitted,
    validateMandatoryFields: validateMandatoryFields,
    changeUtility: changeUtility,
    utilityType: utilityType,
    disableButton: disableButton
  };
  const apiContext = useContext(ApiContext);
  const { notifyMessage } = apiContext;

  const submit = async () => {
    setSubmitted(true);
    const isValid = validateMandatoryFields();
    if (isValid) {
      setActiveIndex(indexes.ProgressTab);
      const requestBody = {
        credential: btoa(
          `${allConnectDetails.username}:${allConnectDetails.password}`
        ),
        serviceProviderId: SelectedUtilityProvider.utilityProviderId,
        serviceProviderName: SelectedUtilityProvider.utilityProviderName,
        subscribed: true,
        pullHistory: true,
        accountDetails: allConnectDetails.accountDetails.map((account) => {
          const utilityDetails = JSON.parse(
            account.utility ? account.utility : ''
          );
          return {
            accountNumber: account.accountNumber?.toString(),
            serviceTypeId: utilityDetails.id
          };
        })
      };
      const result: IAny = await dataConnector.actions.create(requestBody);
      const { notifyDescription, notifyType } = getEnrollNotifyInfo(result);
      notifyMessage && notifyMessage(t(notifyDescription), notifyType);
    }
  };
  return (
    <Modal
      className="emaas-utility-modal"
      size="large"
      closeIcon={true}
      onClose={closeModal}
      open={showModal}
      closeOnDimmerClick={false}
    >
      <div className="utility-modal-header">{'Add Utility Service'}</div>
      <Modal.Content className="utlity-modal-content">
        {activeIndex === indexes.ProgressTab ? (
          <ProgressTab {...ProgressTabProps} />
        ) : (
          <>
            <CustomStepper
              defaultIndex={activeIndex}
              allowHeaderNavigation={false}
            >
              <CustomStepper.Item header={t(d.CHOOSESERVICE)}>
                <ChooseServiceTab {...chooseServiceProps}></ChooseServiceTab>
              </CustomStepper.Item>
              <CustomStepper.Item header={t(d.CONNECT)}>
                <ConnectTab {...connectProps} />
              </CustomStepper.Item>
            </CustomStepper>
            <div className="utility-modal-footer">
              <Button
                type="secondary"
                content={t(d.CANCEL)}
                onClick={closeModal}
              ></Button>
              {activeIndex === indexes.SearchTab && (
                <Button
                  type="primary"
                  disabled={isObjectEmpty(SelectedUtilityProvider)}
                  content={t(d.NEXT)}
                  onClick={() => {
                    setActiveIndex(indexes.ConnectTab);
                  }}
                ></Button>
              )}
              {activeIndex === indexes.ConnectTab && (
                <Button
                  type="primary"
                  content={t(d.SUBMIT)}
                  onClick={() => submit()}
                  disabled={isDisabled}
                ></Button>
              )}
            </div>
          </>
        )}
      </Modal.Content>
    </Modal>
  );
}

export default UtilityServiceModal;
